import { ChangeDetectionStrategy, Component, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'ez-reset-password',
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent {
  username: string;
  forgotPasswordUrl: string;
  errorMessage: string;
  passwordLinkInvalid: boolean;

  readonly #fb = inject(FormBuilder);
  form;

  constructor(elementRef: ElementRef) {
    this.username = elementRef.nativeElement.getAttribute('username');
    this.forgotPasswordUrl = elementRef.nativeElement.getAttribute('forgot-password-url');
    this.errorMessage = elementRef.nativeElement.getAttribute('error-message');
    this.passwordLinkInvalid = elementRef.nativeElement.getAttribute('password-link-invalid') === 'password-link-invalid';

    this.form = this.#fb.group({
      username: [this.username, [Validators.required, Validators.maxLength(64)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(64)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(64), this.createMatchValidator()]],
    });

    // Update the error on the confirm password field any time the password field changes.
    this.form.controls.password.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      const hasError = this.form.controls.password.value !== this.form.controls.confirmPassword.value;

      this.form.controls.confirmPassword.setErrors(hasError ? { passwordMismatch: true } : null);
    });
  }

  changePassword(event: PointerEvent): void {
    if (this.form.invalid) {
      event.preventDefault();
    }
  }

  private createMatchValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const password = this.form?.value.password;

      return password === control.value
        ? null
        : { passwordMismatch: true };
    };
  }
}
